/**
 * The upload queue.
 *
 * Files go into IndexedDB the moment a guest picks them — blob and all — before
 * a single byte is sent. That is the whole point: venue wifi dies, phones lock,
 * people walk out of range mid-upload and close the tab. Anything already in
 * here survives all of that and resumes on the next load.
 *
 * The id is generated on the device and never changes, so a retry re-sends the
 * same file rather than creating a second copy of it.
 *
 * Blobs live in their OWN store, written exactly once.
 *
 *   The first cut kept the blob on the progress record and re-saved the whole
 *   record after every part. A 71 MB video over 9 parts meant 639 MB of writes,
 *   each one slow enough on a phone that progress lagged far behind what the
 *   screen claimed: the UI said 40%, the disk held one part, and a force-quit
 *   lost the difference. Progress rows must stay small enough to commit between
 *   parts, which means the bytes cannot ride along with them.
 */

export type Grant = { name: string; people: 'yes' | 'no' | 'unknown'; device: string };

/**
 * A stable id for this phone, so participation counts people rather than
 * uploads. It identifies no one and never leaves the event it was used in.
 */
export function deviceId(): string {
  try {
    const existing = localStorage.getItem('ec-device');
    if (existing) return existing;
    const made = crypto.randomUUID();
    localStorage.setItem('ec-device', made);
    return made;
  } catch {
    return crypto.randomUUID(); // private mode: counts as a fresh guest, which is the safer error
  }
}

export type QueueState = 'queued' | 'uploading' | 'done' | 'failed';

/** The progress record. Small on purpose — it is written after every part. */
export type QueueItem = {
  id: string;
  slug: string;
  filename: string;
  type: string;
  size: number;
  grant: Grant;
  state: QueueState;
  uploadId?: string;
  parts: { partNumber: number; etag: string }[];
  nextPart: number;
  sent: number;
  attempts: number;
  error?: string;
  addedAt: number;
  /** Legacy rows from v1 carried the blob inline; read, never written. */
  blob?: Blob;
};

const DB_NAME = 'ec-uploads';
const META = 'files';
const BLOBS = 'blobs';

let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbp) return dbp;
  dbp = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: 'id' }).createIndex('slug', 'slug');
      }
      // v2: bytes move out of the progress record.
      if (!db.objectStoreNames.contains(BLOBS)) {
        db.createObjectStore(BLOBS);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbp;
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

/** Never writes bytes: the blob is stripped before the record is saved. */
export async function put(item: QueueItem): Promise<void> {
  const { blob: _drop, ...meta } = item;
  await run(META, 'readwrite', (s) => s.put(meta));
}

export async function get(id: string): Promise<QueueItem | undefined> {
  return run<QueueItem | undefined>(META, 'readonly', (s) => s.get(id));
}

export async function all(slug: string): Promise<QueueItem[]> {
  const items = await run<QueueItem[]>(META, 'readonly', (s) => s.index('slug').getAll(slug));
  return items.sort((a, b) => a.addedAt - b.addedAt);
}

/** v1 rows kept the blob on the record; fall back to it so old queues still drain. */
export async function getBlob(item: QueueItem): Promise<Blob | undefined> {
  const stored = await run<Blob | undefined>(BLOBS, 'readonly', (s) => s.get(item.id));
  return stored ?? item.blob;
}

export async function remove(id: string): Promise<void> {
  await run(META, 'readwrite', (s) => s.delete(id));
  await run(BLOBS, 'readwrite', (s) => s.delete(id));
}

/** Drop the bytes once a file is safely stored — they are large and we are done with them. */
export async function shed(id: string): Promise<void> {
  const item = await get(id);
  await run(BLOBS, 'readwrite', (s) => s.delete(id));
  if (item) await put({ ...item, blob: undefined, state: 'done', sent: item.size });
}

export async function enqueue(slug: string, files: File[], grant: Grant): Promise<QueueItem[]> {
  const added: QueueItem[] = [];
  for (const file of files) {
    const id = crypto.randomUUID();
    // Bytes first: a progress row with no blob behind it would be unrecoverable.
    await run(BLOBS, 'readwrite', (s) => s.put(file, id));
    const item: QueueItem = {
      id,
      slug,
      filename: file.name || 'photo',
      type: file.type || 'application/octet-stream',
      size: file.size,
      grant,
      state: 'queued',
      parts: [],
      nextPart: 1,
      sent: 0,
      attempts: 0,
      addedAt: Date.now(),
    };
    await put(item);
    added.push(item);
  }
  return added;
}

/** IndexedDB is unavailable in some private-browsing modes; say so rather than failing silently. */
export async function usable(): Promise<boolean> {
  try {
    await open();
    return true;
  } catch {
    return false;
  }
}
